import {debounce} from 'lodash';
import {useCallback, useEffect, useMemo, useState} from 'react';

import {addToast} from '@albert-io/atomic';
import {resource} from '@albert-io/json-api-framework/request/builder';
import type {ClassroomModelV1, SubjectModelV2} from '@albert-io/models';
import {useUpdateClassroomSubjectAccessMutation} from 'gql/shared/ClassroomSubjectAccess.queries.generated';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {normalizeError} from 'lib/normalizeError';

import {useStoreChangePump} from '../useStoreChangePump';

// Lifted from the legacy TeacherSubjectsList: the classroom + its subjects (with curriculum area for
// the domain color) and the license context the access toggle reads.
const getClassroomQuery = (classId: string) =>
  resource('classroom_v1')
    .mandarkEndpoint(['classrooms_v1', classId])
    .include('subjects_v2')
    .include('subjects_v2.curriculum_area_v1')
    .include('subjects_v2.guides_v1')
    .withMeta('subject_v2,classroom_v1')
    .customQuery({meta: {context: {is_licensed: true, classroom: {licensed_subjects: true}}}});

export interface ClassSubjects {
  isReady: boolean;
  /** A read failed — show the retry panel instead of the spinner/empty state. */
  hasError: boolean;
  /** Re-dirty the failed reads and refetch. */
  reload: () => void;
  hasNoSubjects: boolean;
  classroom: ClassroomModelV1 | null;
  /** Sorted by name and filtered by the Show-hidden toggle. */
  subjects: SubjectModelV2[];
  /** Licensed teacher at a licensed school — gates the per-card access controls (adaptive is always on). */
  canManageAccess: boolean;
  hasHiddenSubjects: boolean;
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;
  openAccess: (subject: SubjectModelV2) => void;
  togglePartialCredit: (classroomId: string, subject: SubjectModelV2) => void;
  /** The subject pending a close-access confirmation, or null. */
  pendingClose: SubjectModelV2 | null;
  requestCloseAccess: (subject: SubjectModelV2) => void;
  confirmCloseAccess: () => void;
  cancelCloseAccess: () => void;
}

export const useClassSubjects = (classroomId: string): ClassSubjects => {
  const classroomQuery = useMemo(() => getClassroomQuery(classroomId), [classroomId]);
  // Match the legacy: refetch subject access on revisit / class change rather than serving a stale
  // cache. Only a populated cache is invalidated — a cold mount's initial request is already fresh,
  // and dirtying it would fetch the classroom twice.
  useEffect(() => {
    if (classroomQuery.isResourceReady()) {
      classroomQuery.invalidateInterest();
    }
  }, [classroomQuery]);
  const queries = useMemo(() => [classroomQuery], [classroomQuery]);
  const {isReady, resources} = useMandarkQueries(queries);
  const classroom = (resources[0] as ClassroomModelV1) ?? null;

  // Passive Mandark reads don't subscribe React to the store; re-render on store change so a
  // failed fetch surfaces (and a retry re-reads) instead of stalling on the initial spinner.
  useStoreChangePump();

  // A read is invalid once its interest is in an error state. Mandark caches errors forever, so
  // this stays true until reload() re-dirties it.
  const hasError = queries.some((query) => !query.isResourceValid());
  const reload = useCallback(() => {
    queries.forEach((query) => query.invalidateInterest());
  }, [queries]);

  const [showHidden, setShowHidden] = useState(false);
  const [pendingClose, setPendingClose] = useState<SubjectModelV2 | null>(null);
  const [updateClassroomSubjectAccess] = useUpdateClassroomSubjectAccessMutation();

  // Whether the teacher can manage independent-practice access for THIS class = the class is licensed
  // (school active license + teacher approved there + class using seats) Adaptive subjects bypass this downstream.
  // Guard: a mid-retry classroom stub can pass isReady without the meta method.
  const canManageAccess = useMemo(
    () =>
      isReady && typeof classroom?.isLicensed === 'function'
        ? Boolean(classroom.isLicensed())
        : false,
    [isReady, classroom]
  );

  const allSubjects = useMemo(
    () =>
      // During a retry refetch the singular classroom query can momentarily return a defaultModel
      // stub (has getId, but not getSubjects) while isReady is already true — guard against it.
      isReady && classroom && typeof classroom.getSubjects === 'function'
        ? classroom
            .getSubjects()
            .sortBy((subject) => subject.getName().toLowerCase())
            .toArray()
        : [],
    [isReady, classroom]
  );

  const hasHiddenSubjects = allSubjects.some((subject) => subject.isHidden());
  const subjects = showHidden ? allSubjects : allSubjects.filter((subject) => !subject.isHidden());

  const setAccess = async (subject: SubjectModelV2, restricted: boolean) => {
    try {
      const result = await updateClassroomSubjectAccess({
        variables: {
          classroomId,
          subjectId: subject.getId(),
          input: {restrictFreePractice: restricted}
        }
      });
      const errors = result.data?.updateClassroomSubjectAccess?.errors;
      if (errors && errors.length > 0) {
        throw new Error(errors[0]?.message ?? 'Failed to update classroom subject access');
      }
      addToast({
        color: 'positive',
        title: 'Success!',
        message: `Independent practice has been ${
          restricted ? 'disabled' : 'enabled'
        } for ${subject.getName()}.`
      });
      classroomQuery.invalidateInterest();
    } catch (error) {
      addToast({
        color: 'negative',
        title: 'Something went wrong',
        message: normalizeError(error).message
      });
    }
  };

  // Debounced like the legacy: the card flips its optimistic toggle on every change and calls this
  // immediately, so rapid toggling would otherwise fire redundant mutations off stale relationship meta.
  const togglePartialCredit = useMemo(
    () =>
      debounce(async (classId: string, subject: SubjectModelV2) => {
        const partialCreditEnabled =
          subject
            .get('relationshipMeta')
            .get('classroom')
            .get(classId)
            .get('partial_credit_enabled') ?? false;
        try {
          const result = await updateClassroomSubjectAccess({
            variables: {
              classroomId: classId,
              subjectId: subject.getId(),
              input: {partialCreditEnabled: !partialCreditEnabled}
            }
          });
          const errors = result.data?.updateClassroomSubjectAccess?.errors;
          if (errors && errors.length > 0) {
            throw new Error(errors[0]?.message ?? 'Failed to update partial credit setting');
          }
          addToast({
            color: 'positive',
            title: 'Success!',
            message: `Partial credit ${
              partialCreditEnabled ? 'disabled' : 'enabled'
            } for ${subject.getName()}.`
          });
          classroomQuery.invalidateInterest();
        } catch (error) {
          addToast({
            color: 'negative',
            title: 'Something went wrong',
            message: normalizeError(error).message
          });
        }
      }, 250),
    [updateClassroomSubjectAccess, classroomQuery]
  );

  // A pending trailing call must not fire a mutation/toast after unmount or a class switch.
  useEffect(() => () => togglePartialCredit.cancel(), [togglePartialCredit]);

  return {
    isReady,
    hasError,
    reload,
    // isResourceReady excludes the revisit-refetch window, when the cached list is known stale.
    hasNoSubjects: isReady && allSubjects.length === 0 && classroomQuery.isResourceReady(),
    classroom,
    subjects,
    canManageAccess,
    hasHiddenSubjects,
    showHidden,
    setShowHidden,
    openAccess: (subject) => setAccess(subject, false),
    togglePartialCredit,
    pendingClose,
    requestCloseAccess: (subject) => setPendingClose(subject),
    confirmCloseAccess: () => {
      if (pendingClose) {
        setAccess(pendingClose, true);
        setPendingClose(null);
      }
    },
    cancelCloseAccess: () => setPendingClose(null)
  };
};
